import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import {
  DataTableExpandedRows,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableRowEvent,
  DataTableSortEvent,
} from 'primereact/datatable';
import { Message } from 'primereact/message';
import {
  DataGridAtom,
  DataGridColumn,
  SearchBarAtom,
  SwitchAtom,
  useApiClientsOrNull,
} from 'design-system/my-lib';
import './IdentityListPage.scss';

interface IdentitySummary {
  id: string;
  email: string;
  displayName: string | null;
  subjectSphereId: string | null;
  createdAt: string;
  linkedUserCount: number;
  organizationCount: number;
}

interface IdentityOrgEntry {
  organizationId: string;
  organizationName: string;
  userId: string;
  userName: string;
}

type IdentitySortField = 'email' | 'displayName' | 'createdAt';
type SortOrder = 'asc' | 'desc';

const DEFAULT_ROWS = 50;
const ROWS_PER_PAGE = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;
const PRIMEREACT_SORT_TO_ORDER: Record<number, SortOrder> = { 1: 'asc', [-1]: 'desc' };
const ORDER_TO_PRIMEREACT_SORT: Record<SortOrder, 1 | -1> = { asc: 1, desc: -1 };

const IdentityListPage: React.FC = () => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [identities, setIdentities] = useState<IdentitySummary[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [sortField, setSortField] = useState<IdentitySortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [includeE2E, setIncludeE2E] = useState(false);

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const [orgsByIdentity, setOrgsByIdentity] = useState<
    Record<string, { loading: boolean; data: IdentityOrgEntry[] | null; error: string | null }>
  >({});

  useEffect(() => {
    const handle = setTimeout(() => {
      // Batched in React 18: a single render commits both updates, which
      // prevents a stale fetch with the old offset before `first` resets.
      setSearch(searchInput.trim());
      setFirst(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!apiClients?.graphClient) return;
    // Guards against stale responses overwriting fresher state when the user
    // changes search / pagination faster than the network resolves.
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await apiClients.graphClient.get(
          '/admin/identities',
          {
            params: {
              search: search || undefined,
              limit: rows,
              offset: first,
              sortField,
              sortOrder,
              includeE2E: includeE2E || undefined,
            },
          }
        );
        if (cancelled) return;
        setIdentities(response.identities);
        setTotalRecords(response.total);
      } catch {
        if (cancelled) return;
        setError(t('admin.identities.errors.fetchFailed'));
        setIdentities([]);
        setTotalRecords(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClients, search, first, rows, sortField, sortOrder, includeE2E, t]);

  const handlePageChange = useCallback((event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  }, []);

  const handleSort = useCallback((event: DataTableSortEvent) => {
    const field = event.sortField as IdentitySortField;
    if (field !== 'email' && field !== 'displayName' && field !== 'createdAt') return;
    // PrimeReact passes 1 / -1 / 0; treat 0 (cleared) as a return to desc
    // so the table never enters an unsorted state on the server.
    const nextOrder = PRIMEREACT_SORT_TO_ORDER[event.sortOrder ?? -1] ?? 'desc';
    let sortChanged = false;
    setSortField((current) => {
      if (current !== field) sortChanged = true;
      return field;
    });
    setSortOrder((current) => {
      if (current !== nextOrder) sortChanged = true;
      return nextOrder;
    });
    // PrimeReact's DataTable fires onSort alongside onPage; only reset offset
    // when the sort actually changed so pagination clicks aren't trampled.
    if (sortChanged) setFirst(0);
  }, []);

  const handleIncludeE2EChange = useCallback((value: boolean) => {
    setIncludeE2E(value);
    setFirst(0);
  }, []);

  const fetchOrgsForIdentity = useCallback(
    async (identityId: string) => {
      if (!apiClients?.graphClient) return;
      setOrgsByIdentity((prev) => ({
        ...prev,
        [identityId]: { loading: true, data: prev[identityId]?.data ?? null, error: null },
      }));
      try {
        const response = await apiClients.graphClient.get(
          `/admin/identities/${encodeURIComponent(identityId)}/organizations`,
        );
        setOrgsByIdentity((prev) => ({
          ...prev,
          [identityId]: {
            loading: false,
            data: response.organizations || [],
            error: null,
          },
        }));
      } catch {
        setOrgsByIdentity((prev) => ({
          ...prev,
          [identityId]: {
            loading: false,
            data: null,
            error: t('admin.identities.orgs.fetchFailed'),
          },
        }));
      }
    },
    [apiClients, t],
  );

  const ensureOrgsLoaded = useCallback(
    (identityId: string) => {
      const cached = orgsByIdentity[identityId];
      if (!cached || (!cached.loading && !cached.data && !cached.error)) {
        fetchOrgsForIdentity(identityId);
      }
    },
    [orgsByIdentity, fetchOrgsForIdentity],
  );

  const handleRowToggle = useCallback(
    (event: { data: DataTableExpandedRows }) => {
      setExpandedRows(event.data);
    },
    [],
  );

  const handleRowExpand = useCallback(
    (event: DataTableRowEvent) => {
      const row = event.data as IdentitySummary;
      if (row?.id) ensureOrgsLoaded(row.id);
    },
    [ensureOrgsLoaded],
  );

  // Row click toggles expansion in-place (rather than navigating to a detail
  // page). Skip clicks that originated inside interactive controls so they
  // can be reused without bubbling into a row toggle.
  const handleRowClick = useCallback(
    (event: DataTableRowClickEvent) => {
      const target = event.originalEvent.target as HTMLElement;
      if (target?.closest('button, a, input, [role="button"]')) return;
      const row = event.data as IdentitySummary;
      if (!row?.id) return;
      setExpandedRows((prev) => {
        const next = { ...(prev as Record<string, boolean>) };
        if (next[row.id]) {
          delete next[row.id];
        } else {
          next[row.id] = true;
        }
        return next as DataTableExpandedRows;
      });
      ensureOrgsLoaded(row.id);
    },
    [ensureOrgsLoaded],
  );

  const emptyMessage = useMemo(
    () =>
      search
        ? t('admin.identities.emptyNoResults')
        : t('admin.identities.empty'),
    [search, t]
  );

  const formatDate = useCallback((value: string) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? value : date.toLocaleDateString();
  }, []);

  const createdAtTemplate = useCallback(
    (rowData: IdentitySummary) => <span>{formatDate(rowData.createdAt)}</span>,
    [formatDate],
  );

  const emailTemplate = useCallback(
    (rowData: IdentitySummary) => <span>{rowData.email || '—'}</span>,
    [],
  );

  const displayNameTemplate = useCallback(
    (rowData: IdentitySummary) => <span>{rowData.displayName || '—'}</span>,
    [],
  );

  const linkedUserCountTemplate = useCallback(
    (rowData: IdentitySummary) => (
      <span className="identity-list-page__metric">{rowData.linkedUserCount}</span>
    ),
    [],
  );

  const orgCountTemplate = useCallback(
    (rowData: IdentitySummary) => (
      <span className="identity-list-page__metric">{rowData.organizationCount}</span>
    ),
    [],
  );

  const columns: DataGridColumn[] = useMemo(
    () => [
      {
        field: 'email',
        header: t('admin.identities.columns.email'),
        body: emailTemplate,
        sortable: true,
      },
      {
        field: 'displayName',
        header: t('admin.identities.columns.displayName'),
        body: displayNameTemplate,
        sortable: true,
      },
      {
        field: 'createdAt',
        header: t('admin.identities.columns.created'),
        body: createdAtTemplate,
        sortable: true,
        width: '140px',
      },
      {
        field: 'linkedUserCount',
        header: t('admin.identities.columns.linkedUsers'),
        body: linkedUserCountTemplate,
        width: '120px',
      },
      {
        field: 'organizationCount',
        header: t('admin.identities.columns.orgs'),
        body: orgCountTemplate,
        width: '90px',
      },
    ],
    [t, emailTemplate, displayNameTemplate, createdAtTemplate, linkedUserCountTemplate, orgCountTemplate],
  );

  // ---------- Tray (row expansion content) ----------

  const rowExpansionTemplate = useCallback(
    (rowData: IdentitySummary) => {
      const cached = orgsByIdentity[rowData.id];

      const detailsBlock = (
        <dl className="identity-tray__details">
          <div className="identity-tray__details-row">
            <dt>{t('admin.identities.tray.email')}</dt>
            <dd>{rowData.email || '—'}</dd>
          </div>
          <div className="identity-tray__details-row">
            <dt>{t('admin.identities.tray.displayName')}</dt>
            <dd>{rowData.displayName || '—'}</dd>
          </div>
          <div className="identity-tray__details-row">
            <dt>{t('admin.identities.tray.created')}</dt>
            <dd>{formatDate(rowData.createdAt)}</dd>
          </div>
          <div className="identity-tray__details-row">
            <dt>{t('admin.identities.tray.id')}</dt>
            <dd>
              <code className="identity-tray__id">{rowData.id}</code>
            </dd>
          </div>
        </dl>
      );

      let orgsContent: React.ReactNode;
      if (!cached || cached.loading) {
        orgsContent = (
          <div className="identity-tray__orgs-state">
            <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            <span>{t('admin.identities.orgs.loading')}</span>
          </div>
        );
      } else if (cached.error) {
        orgsContent = (
          <div className="identity-tray__orgs-state identity-tray__orgs-state--error">
            {cached.error}
          </div>
        );
      } else if ((cached.data ?? []).length === 0) {
        orgsContent = (
          <div className="identity-tray__orgs-state">
            {t('admin.identities.orgs.empty')}
          </div>
        );
      } else {
        orgsContent = (
          <table className="identity-tray__orgs-table">
            <thead>
              <tr>
                <th>{t('admin.identities.orgs.organization')}</th>
                <th>{t('admin.identities.orgs.user')}</th>
              </tr>
            </thead>
            <tbody>
              {(cached.data ?? []).map((entry) => (
                <tr key={`${entry.organizationId}:${entry.userId}`}>
                  <td>{entry.organizationName || '—'}</td>
                  <td>{entry.userName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      return (
        <div className="identity-tray">
          <section className="identity-tray__section">
            <h3 className="identity-tray__section-title">
              {t('admin.identities.tray.detailsTitle')}
            </h3>
            {detailsBlock}
          </section>
          <section className="identity-tray__section">
            <h3 className="identity-tray__section-title">
              {t('admin.identities.tray.orgsTitle')}
            </h3>
            {orgsContent}
          </section>
        </div>
      );
    },
    [orgsByIdentity, formatDate, t],
  );

  return (
    <div className="identity-list-page" data-testid="identity-list-page">
      <header className="identity-list-page__page-header">
        <h1 className="identity-list-page__title">{t('admin.identities.title')}</h1>
      </header>

      <div className="identity-list-page__controls">
        <SwitchAtom
          inputId="identity-list-include-e2e"
          label={t('admin.identities.toggleIncludeE2E')}
          checked={includeE2E}
          onChange={handleIncludeE2EChange}
        />
        <div className="identity-list-page__search">
          <SearchBarAtom
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('admin.identities.searchPlaceholder')}
          />
        </div>
      </div>

      {error ? (
        <Message
          severity="error"
          text={error}
          className="identity-list-page__error"
        />
      ) : (
        <DataGridAtom
          data={identities}
          columns={columns}
          loading={loading}
          paginator
          rows={rows}
          rowsPerPageOptions={ROWS_PER_PAGE}
          first={first}
          onPage={handlePageChange}
          totalRecords={totalRecords}
          lazy
          onRowClick={handleRowClick}
          rowClassName={() => 'identity-list-page__row'}
          emptyMessage={emptyMessage}
          dataKey="id"
          showHeader={false}
          expandedRows={expandedRows}
          onRowToggle={handleRowToggle}
          onRowExpand={handleRowExpand}
          rowExpansionTemplate={rowExpansionTemplate}
          sortField={sortField}
          sortOrder={ORDER_TO_PRIMEREACT_SORT[sortOrder]}
          onSort={handleSort}
          className="identity-list-page__table"
          data-testid="identity-list-table"
        />
      )}
    </div>
  );
};

export default IdentityListPage;
